import React, { useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { getApiBase } from "../lib/apiBase";

const logoUrl = "https://customer-assets.emergentagent.com/job_bim-solutions/artifacts/m8dxb8rv_Final%20logo%20lone%20jpeg.jpg";

const safeValue = (value) => {
  if (value === undefined || value === null) return "À renseigner";
  const normalized = String(value).trim();
  return normalized ? normalized : "À renseigner";
};

const money = (value) => `${Math.round(value || 0).toLocaleString("fr-FR")} €`;

const getOfferTitle = (mode) => (mode === "4D" ? "Offre estimative BIM 4D + Planning" : "Offre estimative BIM 3D + Synthèse");

const getDisplayDelay = (state) => safeValue(state.urgency);

const get3DBreakdownRows = (state, totalDays, unitRate) => {
  const urgencyFactor = state.urgency === "Urgent" ? 1.3 : 1.0;
  const preUrgencyTotal = urgencyFactor ? totalDays / urgencyFactor : totalDays;
  const plansBase = (state.plans || 0) * 0.4;
  const modelingBase = Math.max(0, state.synthesis ? (preUrgencyTotal - plansBase) / 2 : preUrgencyTotal - plansBase);
  const synthesisBase = state.synthesis ? modelingBase : 0;
  const rows = [
    { label: "Modélisation BIM 3D", days: modelingBase * urgencyFactor },
    { label: "Synthèse BIM", days: synthesisBase * urgencyFactor },
    { label: "Sortie de plans", days: plansBase * urgencyFactor },
  ];
  return rows.map((row) => ({ ...row, amount: row.days * unitRate }));
};

const get4DBreakdownRows = (breakdown, totalDays, unitRate) => {
  if (breakdown) {
    const rows = [
      { label: "Base surface", days: breakdown.baseSurfaceDays || 0 },
      { label: "Base tâches à relier", days: breakdown.baseTaskDays || 0 },
      { label: "Facteurs livrables", days: Math.max(0, (breakdown.afterDeliverablesDays || 0) - (breakdown.baseDays || 0)) },
      { label: "Suivi chantier", days: breakdown.followUpDays || 0 },
    ];
    return rows.map((row) => ({ ...row, amount: row.days * unitRate }));
  }

  const rows = [{ label: "Mission BIM 4D + Planning", days: totalDays }];
  return rows.map((row) => ({ ...row, amount: row.days * unitRate }));
};

const getMissionItems = (mode, state) => {
  if (mode === "4D") {
    const items = [
      "Préparation et structuration du planning projet selon les données fournies",
      "Liaison maquette-planning et séquençage des phases clés",
      "Simulation visuelle BIM 4D et exports associés selon le périmètre retenu",
      "Restitution numérique exploitable pour le pilotage du projet",
    ];
    if (state.d4?.siteFollowUp) {
      items.splice(3, 0, `Suivi de chantier indicatif sur ${state.d4?.followUpWeeks || 0} semaine(s) en intensité ${state.d4?.followUpIntensity || "Standard"}`);
    }
    return items;
  }

  const items = [
    "Production de maquettes BIM 3D selon les disciplines retenues",
    "Structuration des catégories, familles, types et niveaux de détail",
    "Coordination entre les disciplines sélectionnées",
    "Extraction de quantités de base depuis la maquette",
    "Livrables numériques au format convenu",
  ];

  if (state.synthesis) {
    items.splice(3, 0, "Détection et suivi des conflits si l’option Synthèse BIM est activée");
  }

  if ((state.plans || 0) > 0) {
    items.push(`Production de ${state.plans} plan(s) selon le nombre renseigné`);
  }

  return items;
};

const assumptions = [
  "Surface et disciplines déclarées par le client",
  "Niveau LOD / LOI sélectionné",
  "Documents projet supposés exploitables",
  "Étages types supposés cohérents sauf indication contraire",
];

const exclusions = [
  "Études d’ingénierie et calculs réglementaires",
  "Licences BIM 360 / Autodesk Construction Cloud / logiciels tiers si nécessaires",
  "Modifications majeures non prévues",
  "Plans supplémentaires non renseignés",
  "Reprises dues à des documents incomplets ou contradictoires",
];

const conditions = [
  "Estimation indicative non contractuelle",
  "Devis ferme après analyse des pièces projet",
  "Cycles de corrections inclus selon périmètre défini",
  "Changements majeurs facturés au temps passé",
  "Acompte éventuel avant démarrage selon devis officiel",
];

const getPlanningRows = (mode, breakdownRows, totalDays) => {
  const totalWeeks = Math.max(1, Math.ceil(totalDays / 5));

  let phases = [];

  if (mode === "4D") {
    phases = [
      { label: "Cadrage & préparation", days: totalDays * 0.15, tone: "teal" },
      { label: "Liaison maquette-planning", days: totalDays * 0.5, tone: "cyan" },
      { label: "Simulation / exports", days: totalDays * 0.22, tone: "lime" },
      { label: "Contrôle qualité / livraison", days: totalDays * 0.13, tone: "yellow" },
    ];
  } else {
    const modelingDays = breakdownRows.find((row) => row.label === "Modélisation BIM 3D")?.days || 0;
    const synthesisDays = breakdownRows.find((row) => row.label === "Synthèse BIM")?.days || 0;
    const plansDays = breakdownRows.find((row) => row.label === "Sortie de plans")?.days || 0;
    const preparationDays = Math.max(0.4, modelingDays * 0.12);
    const qualityDays = Math.max(0.4, modelingDays * 0.08);
    const productionDays = Math.max(0.5, modelingDays - preparationDays - qualityDays);

    phases = [
      { label: "Cadrage & préparation", days: preparationDays, tone: "teal" },
      { label: "Modélisation BIM 3D", days: productionDays, tone: "cyan" },
      ...(synthesisDays > 0 ? [{ label: "Synthèse / coordination", days: synthesisDays, tone: "lime" }] : []),
      ...(plansDays > 0 ? [{ label: "Sortie de plans", days: plansDays, tone: "yellow" }] : []),
      { label: "Contrôle qualité / livraison", days: qualityDays, tone: "teal" },
    ];
  }

  let currentDay = 0;
  const rows = phases.map((phase) => {
    const startWeek = Math.floor(currentDay / 5) + 1;
    const endDay = currentDay + phase.days;
    const endWeek = Math.ceil(endDay / 5);
    const spanWeeks = Math.max(1, endWeek - startWeek + 1);
    currentDay = endDay;
    return {
      ...phase,
      startWeek,
      spanWeeks,
    };
  });

  return {
    totalWeeks,
    rows,
  };
};

export const QuotePreview = ({ open, onOpenChange, data, loiPhases, mode = "3D", adminDetail, unitRate }) => {
  if (!data) return null;

  const { state, price, days } = data;
  const loiObj = (loiPhases || []).find((phase) => phase.label === state.loiPhase);
  const today = useMemo(() => new Date(), [open]);
  const dateStr = useMemo(() => today.toLocaleDateString("fr-FR"), [today]);
  const offerNo = useMemo(() => {
    const ymd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
    const rnd = Math.floor(Math.random() * 900 + 100);
    return `BC-${ymd}-${rnd}`;
  }, [today]);

  const breakdownRows = useMemo(
    () => (mode === "4D" ? get4DBreakdownRows(adminDetail?.breakdown, days, unitRate) : get3DBreakdownRows(state, days, unitRate)),
    [mode, state, days, unitRate, adminDetail]
  );

  const totalAmount = breakdownRows.reduce((sum, row) => sum + row.amount, 0);
  const missionItems = useMemo(() => getMissionItems(mode, state), [mode, state]);
  const planning = useMemo(() => getPlanningRows(mode, breakdownRows, days), [mode, breakdownRows, days]);
  const assumptionsList = useMemo(() => {
    if (mode !== "4D") return assumptions;
    return [
      "Surface retenue minimum : 1 000 m²",
      "Nombre minimum de tâches retenu : 20",
      "Livrable de base inclus : Maquette + Planning + PDF",
      ...(state.d4?.siteFollowUp ? [`Suivi de chantier indicatif activé sur ${state.d4?.followUpWeeks || 0} semaine(s) en intensité ${state.d4?.followUpIntensity || "Standard"}`] : []),
      "Documents projet supposés exploitables et planning cohérent",
    ];
  }, [mode, state]);
  const optionsText = useMemo(() => {
    const options = [];
    if (mode === "3D") {
      options.push(state.synthesis ? "Synthèse BIM activée" : "Synthèse BIM non activée");
      options.push((state.plans || 0) > 0 ? `${state.plans} plan(s) prévus` : "Pas de sortie de plans renseignée");
      if ((state.specialSurfaces?.vrd || 0) > 0) {
        options.push(`Surfaces spécifiques : ${(state.specialSurfaces?.vrd || 0).toLocaleString("fr-FR")} m²`);
      }
    } else {
      options.push("Maquette + Planning + PDF inclus par défaut");
      if (state.d4?.videoExport) options.push("Exports vidéo activés");
      if (state.d4?.navisworksSynchro) options.push("Navisworks / Synchro activé");
      if (state.d4?.siteFollowUp) {
        options.push(`Suivi chantier : ${state.d4?.followUpWeeks || 0} semaine(s) • ${state.d4?.followUpIntensity || "Standard"}`);
      }
    }
    return options;
  }, [mode, state]);

  const executiveSummary = mode === "4D"
    ? `Sur la base des paramètres renseignés, Bimcubes estime la mission à ${days.toFixed(3)} jours-homme, pour un montant indicatif de ${money(price)} HT. Cette estimation sera confirmée après analyse des documents projet, du niveau de structuration attendu et des livrables 4D demandés.${state.d4?.siteFollowUp ? ` Cette estimation inclut également un suivi de chantier indicatif de ${state.d4?.followUpWeeks || 0} semaines en intensité ${state.d4?.followUpIntensity || "Standard"}.` : ""}`
    : `Sur la base des paramètres renseignés, Bimcubes estime la mission à ${days.toFixed(3)} jours-homme, pour un montant indicatif de ${money(price)} HT. Cette estimation sera confirmée après analyse des documents projet, du niveau de détail attendu et des livrables demandés.`;

  const buildHtml = () => {
    const clientHtml = `
      <div class="detail-card">
        <h3>Client</h3>
        <ul class="detail-list">
          <li><strong>Société :</strong> ${safeValue(state.company)}</li>
          <li><strong>Contact :</strong> ${safeValue(state.name)}</li>
          <li><strong>Email :</strong> ${safeValue(state.email)}</li>
          <li><strong>Téléphone :</strong> ${safeValue(state.phone)}</li>
          <li><strong>Adresse :</strong> ${safeValue(state.clientAddress)}</li>
        </ul>
      </div>`;

    const projectHtml = mode === "4D" ? `
      <div class="detail-card">
        <h3>Projet</h3>
        <ul class="detail-list">
          <li><strong>Type de projet :</strong> ${safeValue(state.projectType)}</li>
          <li><strong>Surface :</strong> ${safeValue(state.area)} m²</li>
          <li><strong>Tâches à relier :</strong> ${safeValue(state.d4?.tasks)}</li>
          <li><strong>Livrables :</strong> ${optionsText.length ? optionsText.join(" • ") : "À renseigner"}</li>
          <li><strong>Suivi chantier :</strong> ${state.d4?.siteFollowUp ? `${safeValue(state.d4?.followUpWeeks)} semaines • ${safeValue(state.d4?.followUpIntensity)}` : "Non activé"}</li>
        </ul>
      </div>` : `
      <div class="detail-card">
        <h3>Projet</h3>
        <ul class="detail-list">
          <li><strong>Type de projet :</strong> ${safeValue(state.projectType)}</li>
          <li><strong>Surface :</strong> ${safeValue(state.area)} m²</li>
          <li><strong>LOD :</strong> ${safeValue(state.lod)}</li>
          <li><strong>Phase / LOI :</strong> ${safeValue(loiObj?.loi)} (${safeValue(state.loiPhase)})</li>
          <li><strong>Disciplines :</strong> ${safeValue((state.discipline || []).join(", "))}</li>
          <li><strong>Délai :</strong> ${getDisplayDelay(state)}</li>
          <li><strong>Options :</strong> ${optionsText.length ? optionsText.join(" • ") : "À renseigner"}</li>
        </ul>
      </div>`;

    const kpiHtml = [
      { label: "Total jours-homme", value: `${days.toFixed(3)} j-h` },
      { label: "Montant HT", value: `${money(price)} HT` },
      { label: "Taux journalier", value: `${money(unitRate)} / j-h` },
      { label: "Délai", value: getDisplayDelay(state) },
    ]
      .map(
        (item) => `
          <div class="kpi-card">
            <div class="kpi-label">${item.label}</div>
            <div class="kpi-value">${item.value}</div>
          </div>`
      )
      .join("");

    const rowsHtml = breakdownRows
      .map(
        (row) => `
          <tr>
            <td>${row.label}</td>
            <td>${row.days.toFixed(3)} j-h</td>
            <td>${money(row.amount)} HT</td>
          </tr>`
      )
      .join("");
    const weekHeadersHtml = Array.from({ length: planning.totalWeeks }, (_, index) => `<div class="planning-week-header">S${index + 1}</div>`).join("");
    const planningHtml = planning.rows
      .map(
        (row) => `
          <div class="planning-row">
            <div class="planning-label">${row.label}</div>
            <div class="planning-weeks" style="grid-template-columns: repeat(${planning.totalWeeks}, minmax(0, 1fr));">
              ${Array.from({ length: planning.totalWeeks }, () => `<div class="planning-week-cell"></div>`).join("")}
              <div class="planning-bar ${row.tone}" style="grid-column: ${row.startWeek} / span ${row.spanWeeks};"></div>
            </div>
          </div>`
      )
      .join("");

    const missionHtml = missionItems.map((item) => `<li>${item}</li>`).join("");
    const assumptionsHtml = assumptionsList.map((item) => `<li>${item}</li>`).join("");
    const exclusionsHtml = exclusions.map((item) => `<li>${item}</li>`).join("");
    const conditionsHtml = conditions.map((item) => `<li>${item}</li>`).join("");

    return `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${getOfferTitle(mode)}</title>
        <style>
          :root {
            --ink: #0f172a;
            --muted: #64748b;
            --line: #d9e7d5;
            --surface: #ffffff;
            --surface-soft: #f8fcf3;
            --accent: #0f766e;
            --accent-soft: #ecfdf5;
          }
          * { box-sizing: border-box; }
          body { margin: 0; padding: 32px; font-family: Arial, Helvetica, sans-serif; color: var(--ink); background: #fff; }
          h1, h2, h3 { margin: 0; }
          .page { display: grid; gap: 24px; }
          .header { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 24px; align-items: start; padding: 20px; border: 1px solid var(--line); border-radius: 20px; background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,252,243,0.95)); }
          .brand { display: flex; gap: 14px; align-items: center; }
          .brand img { height: 56px; width: auto; }
          .company-block { display: grid; gap: 4px; }
          .muted { color: var(--muted); }
          .offer-title { font-size: 28px; line-height: 1.1; font-weight: 700; }
          .offer-meta { display: grid; gap: 8px; justify-items: end; text-align: right; font-size: 13px; }
          .badge { display: inline-flex; padding: 8px 12px; border-radius: 999px; background: var(--accent-soft); color: var(--accent); font-weight: 700; font-size: 12px; }
          .grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
          .detail-card, .section-card { border: 1px solid var(--line); border-radius: 18px; background: var(--surface); padding: 20px; }
          .detail-card h3, .section-card h2 { margin-bottom: 14px; }
          .detail-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 9px; font-size: 14px; }
          .summary-text { font-size: 15px; line-height: 1.8; color: #334155; }
          .kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-top: 18px; }
          .kpi-card { border: 1px solid var(--line); border-radius: 16px; background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(240,253,244,0.9)); padding: 16px; }
          .kpi-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); }
          .kpi-value { margin-top: 10px; font-size: 22px; line-height: 1.2; font-weight: 700; color: var(--accent); }
          .planning-card { margin-top: 20px; border: 1px solid var(--line); border-radius: 16px; background: var(--surface); padding: 18px; }
          .planning-grid { display: grid; gap: 12px; margin-top: 14px; }
          .planning-header { display: grid; grid-template-columns: 12rem 1fr; gap: 12px; align-items: center; margin-top: 14px; }
          .planning-week-headings { display: grid; gap: 8px; }
          .planning-week-header { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; text-align: center; }
          .planning-row { display: grid; grid-template-columns: 12rem 1fr; gap: 12px; align-items: center; }
          .planning-label { font-size: 13px; color: #334155; }
          .planning-weeks { position: relative; display: grid; gap: 8px; align-items: center; }
          .planning-week-cell { height: 16px; border-radius: 999px; background: #eef6f2; }
          .planning-bar { height: 14px; border-radius: 999px; align-self: center; z-index: 2; box-shadow: 0 8px 16px rgba(15,118,110,0.12); }
          .planning-bar.teal { background: linear-gradient(90deg, #0f766e, #2dd4bf); }
          .planning-bar.cyan { background: linear-gradient(90deg, #14b8a6, #5eead4); }
          .planning-bar.lime { background: linear-gradient(90deg, #84cc16, #bef264); }
          .planning-bar.yellow { background: linear-gradient(90deg, #fde047, #facc15); }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th, td { border-bottom: 1px solid #e5e7eb; padding: 14px 10px; text-align: left; }
          th { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); }
          .total-row td { font-weight: 700; color: var(--ink); border-top: 2px solid var(--line); }
          .bullet-list { margin: 0; padding-left: 18px; display: grid; gap: 10px; line-height: 1.7; color: #334155; }
          .grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
          .signature-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin-top: 20px; }
          .signature-box { border: 1px dashed #cbd5e1; border-radius: 16px; min-height: 120px; background: var(--surface-soft); }
          .section-subtitle { margin-top: 6px; color: var(--muted); font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="page">
          <section class="header">
            <div class="brand">
              <img src="${logoUrl}" alt="Bimcubes" />
              <div class="company-block">
                <strong>Bimcubes</strong>
                <span class="muted">10 rue du Colisée, 75008 Paris</span>
                <span class="muted">contact@bimcubes.fr</span>
              </div>
            </div>
            <div class="offer-meta">
              <div class="offer-title">${getOfferTitle(mode)}</div>
              <div><strong>N° d’offre :</strong> ${offerNo}</div>
              <div><strong>Date :</strong> ${dateStr}</div>
              <div><strong>SIREN :</strong> 901 650 887 00010</div>
              <div><strong>NAF/APE :</strong> 7490B</div>
              <div><strong>N° TVA :</strong> FR03901650887</div>
              <span class="badge">Estimation indicative — non contractuelle</span>
            </div>
          </section>

          <section class="grid-2">
            ${clientHtml}
            ${projectHtml}
          </section>

          <section class="section-card">
            <h2>Résumé exécutif</h2>
            <p class="summary-text">${executiveSummary}</p>
            <div class="kpis">${kpiHtml}</div>
            <div class="planning-card">
              <h3>Planning indicatif</h3>
              <p class="section-subtitle">Lecture visuelle indicative des principales phases de production.</p>
              <div class="planning-header">
                <div></div>
                <div class="planning-week-headings" style="grid-template-columns: repeat(${planning.totalWeeks}, minmax(0, 1fr));">${weekHeadersHtml}</div>
              </div>
              <div class="planning-grid">${planningHtml}</div>
            </div>
          </section>

          <section class="section-card">
            <h2>Détail de l’estimation</h2>
            <table>
              <thead>
                <tr><th>Poste</th><th>Jours-homme</th><th>Montant HT</th></tr>
              </thead>
              <tbody>
                ${rowsHtml}
                <tr class="total-row"><td>Total</td><td>${days.toFixed(3)} j-h</td><td>${money(totalAmount)} HT</td></tr>
              </tbody>
            </table>
          </section>

          <section class="section-card">
            <h2>Périmètre de mission inclus</h2>
            <p class="section-subtitle">Selon les paramètres sélectionnés, l’estimation peut inclure les prestations suivantes :</p>
            <ul class="bullet-list">${missionHtml}</ul>
          </section>

          <section class="grid-3">
            <div class="section-card">
              <h2>Hypothèses</h2>
              <ul class="bullet-list">${assumptionsHtml}</ul>
            </div>
            <div class="section-card">
              <h2>Exclusions</h2>
              <ul class="bullet-list">${exclusionsHtml}</ul>
            </div>
            <div class="section-card">
              <h2>Conditions</h2>
              <ul class="bullet-list">${conditionsHtml}</ul>
            </div>
          </section>

          <section class="section-card">
            <h2>Prochaine étape</h2>
            <p class="summary-text">Pour transformer cette estimation en devis officiel, Bimcubes analysera les documents transmis : plans, maquettes existantes, CCTP, DPGF, planning ou cahier des charges BIM.</p>
            <div class="signature-grid">
              <div>
                <strong>Signature Bimcubes</strong>
                <div class="signature-box"></div>
                <div class="section-subtitle">Nom, date, signature</div>
              </div>
              <div>
                <strong>Signature Client</strong>
                <div class="signature-box"></div>
                <div class="section-subtitle">Nom, date, signature</div>
              </div>
            </div>
          </section>
        </div>
      </body>
    </html>`;
  };

  const buildAdminHtml = () => {
    const pretty = `<pre style="font-size:12px;background:#0f172a;color:#e2e8f0;padding:12px;border-radius:12px;overflow:auto">${JSON.stringify({ unitRate: unitRate || null, calcDetail: adminDetail || null }, null, 2)}</pre>`;
    return buildHtml().replace("</body>", `<section style="margin-top:24px"><h2 style="font-family:Arial,Helvetica,sans-serif;color:#0f172a">Informations internes Bimcubes</h2>${pretty}</section></body>`);
  };

  const downloadHtml = () => {
    const blob = new Blob([buildHtml()], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `offre-estimative-bimcubes-${Date.now()}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    const html = buildHtml();
    const popup = window.open("", "_blank");
    if (!popup) return toast.error("Impossible d'ouvrir la fenêtre d'impression.");
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    setTimeout(() => popup.print(), 150);
  };

  const requestOfficialQuote = async () => {
    try {
      const backendBase = getApiBase();
      const apiBase = backendBase?.endsWith("/api") ? backendBase : `${backendBase}/api`;
      await axios.post(
        `${apiBase}/quotes/request-official`,
        {
          public_html: buildHtml(),
          admin_html: buildAdminHtml(),
          meta: {
            offerNo,
            dateStr,
            mode,
            days,
            price,
            unitRate: unitRate || null,
            client: {
              company: state.company,
              name: state.name,
              email: state.email,
              phone: state.phone,
              address: state.clientAddress,
            },
            project: {
              area: state.area,
              lod: state.lod,
              loi: loiObj?.loi,
              loiPhase: state.loiPhase,
              disciplines: state.discipline || [],
              specialSurfaceShared: state.specialSurfaces?.vrd || 0,
              synthesis: state.synthesis,
              plans: state.plans || 0,
              urgency: state.urgency,
            },
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      toast.success("Demande envoyée. Nous revenons vers vous rapidement.");
    } catch (error) {
      console.error(error);
      toast.error("Impossible d'envoyer la demande pour le moment.");
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[88vh] max-w-6xl overflow-y-auto" data-testid="quote-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-950" data-testid="quote-preview-title">
            Aperçu de l’offre estimative
          </DialogTitle>
          <DialogDescription className="text-slate-600" data-testid="quote-preview-description">
            Offre estimative — à confirmer après analyse des documents.
          </DialogDescription>
        </DialogHeader>

        <section className="grid gap-5 rounded-[2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6 md:grid-cols-[1.05fr_0.95fr]" data-testid="quote-preview-header-section">
          <div className="flex items-center gap-4">
            <img alt="Bimcubes" className="h-14 w-auto rounded-2xl border border-[#d9e7d5] bg-white p-2" data-testid="quote-preview-logo" src={logoUrl} />
            <div className="grid gap-1">
              <div className="font-semibold text-slate-950" data-testid="quote-preview-company-name">Bimcubes</div>
              <div className="text-sm text-slate-600">10 rue du Colisée, 75008 Paris</div>
              <div className="text-sm text-slate-600">contact@bimcubes.fr</div>
            </div>
          </div>

          <div className="grid justify-items-start gap-2 text-sm text-slate-700 md:justify-items-end md:text-right">
            <div className="text-2xl font-semibold text-slate-950" data-testid="quote-preview-offer-heading">{getOfferTitle(mode)}</div>
            <div><strong>N° d’offre :</strong> <span data-testid="quote-preview-offer-number">{offerNo}</span></div>
            <div><strong>Date :</strong> <span data-testid="quote-preview-offer-date">{dateStr}</span></div>
            <div><strong>SIREN :</strong> 901 650 887 00010</div>
            <div><strong>NAF/APE :</strong> 7490B</div>
            <div><strong>N° TVA :</strong> FR03901650887</div>
            <Badge className="border border-[#b7e4c7] bg-[#ecfdf5] text-[#0f766e]" data-testid="quote-preview-offer-badge">Estimation indicative — non contractuelle</Badge>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2" data-testid="quote-preview-client-project-grid">
          <Card className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-white/90 shadow-[0_22px_60px_-48px_rgba(15,118,110,0.16)]" data-testid="quote-preview-client-card">
            <CardContent className="p-6">
              <h3 className="bim-display text-xl font-semibold text-slate-950">Client</h3>
              <div className="mt-5 grid gap-3 text-sm text-slate-700">
                <div><span className="text-slate-500">Société :</span> {safeValue(state.company)}</div>
                <div><span className="text-slate-500">Contact :</span> {safeValue(state.name)}</div>
                <div><span className="text-slate-500">Email :</span> {safeValue(state.email)}</div>
                <div><span className="text-slate-500">Téléphone :</span> {safeValue(state.phone)}</div>
                <div><span className="text-slate-500">Adresse :</span> {safeValue(state.clientAddress)}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-white/90 shadow-[0_22px_60px_-48px_rgba(15,118,110,0.16)]" data-testid="quote-preview-project-card">
            <CardContent className="p-6">
              <h3 className="bim-display text-xl font-semibold text-slate-950">Projet</h3>
              <div className="mt-5 grid gap-3 text-sm text-slate-700">
                <div><span className="text-slate-500">Type de projet :</span> {safeValue(state.projectType)}</div>
                <div><span className="text-slate-500">Surface :</span> {safeValue(state.area)} m²</div>
                {mode === "4D" ? (
                  <>
                    <div><span className="text-slate-500">Tâches à relier :</span> {safeValue(state.d4?.tasks)}</div>
                    <div><span className="text-slate-500">Suivi chantier :</span> {state.d4?.siteFollowUp ? `${safeValue(state.d4?.followUpWeeks)} semaines • ${safeValue(state.d4?.followUpIntensity)}` : "Non activé"}</div>
                  </>
                ) : (
                  <>
                    <div><span className="text-slate-500">LOD :</span> {safeValue(state.lod)}</div>
                    <div><span className="text-slate-500">Phase / LOI :</span> {safeValue(loiObj?.loi)} ({safeValue(state.loiPhase)})</div>
                    <div><span className="text-slate-500">Disciplines :</span> {safeValue((state.discipline || []).join(", "))}</div>
                    <div><span className="text-slate-500">Délai :</span> {getDisplayDelay(state)}</div>
                  </>
                )}
                <div><span className="text-slate-500">Options :</span> {optionsText.length ? optionsText.join(" • ") : "À renseigner"}</div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6" data-testid="quote-preview-executive-summary">
          <h3 className="bim-display text-xl font-semibold text-slate-950">Résumé exécutif</h3>
          <p className="mt-4 text-base leading-8 text-slate-600" data-testid="quote-preview-executive-text">{executiveSummary}</p>
          <div className="mt-6 grid gap-4 md:grid-cols-4" data-testid="quote-preview-kpis-grid">
            {[
              { label: "Total jours-homme", value: `${days.toFixed(3)} j-h`, testId: "quote-preview-kpi-days" },
              { label: "Montant HT", value: `${money(price)} HT`, testId: "quote-preview-kpi-price" },
              { label: "Taux journalier", value: `${money(unitRate)} / j-h`, testId: "quote-preview-kpi-rate" },
              { label: "Délai", value: getDisplayDelay(state), testId: "quote-preview-kpi-delay" },
            ].map((item) => (
              <Card className="rounded-[1.4rem] border border-[#d9e7d5]/80 bg-white/92 shadow-[0_18px_40px_-32px_rgba(15,118,110,0.14)]" data-testid={item.testId} key={item.label}>
                <CardContent className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{item.label}</div>
                  <div className="mt-3 text-2xl font-semibold text-[#0f766e]">{item.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="mt-6 rounded-[1.4rem] border border-[#d9e7d5]/80 bg-white/92 shadow-[0_18px_40px_-32px_rgba(15,118,110,0.12)]" data-testid="quote-preview-planning-card">
            <CardContent className="p-5">
              <div className="text-lg font-semibold text-slate-950">Planning indicatif</div>
              <div className="mt-2 text-sm text-slate-500">Lecture visuelle indicative des principales phases de production.</div>
              <div className="mt-5 grid gap-3 md:grid-cols-[12rem_1fr] md:items-center" data-testid="quote-preview-planning-header">
                <div></div>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${planning.totalWeeks}, minmax(0, 1fr))` }}>
                  {Array.from({ length: planning.totalWeeks }, (_, index) => (
                    <div className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500" key={`week-${index + 1}`}>S{index + 1}</div>
                  ))}
                </div>
              </div>
              <div className="mt-4 grid gap-4" data-testid="quote-preview-planning-grid">
                {planning.rows.map((row) => (
                  <div className="grid gap-3 md:grid-cols-[12rem_1fr] md:items-center" key={row.label}>
                    <div className="text-sm text-slate-700">{row.label}</div>
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${planning.totalWeeks}, minmax(0, 1fr))` }}>
                      {Array.from({ length: planning.totalWeeks }, (_, index) => (
                        <div className="h-4 rounded-full bg-[#eef6f2]" key={`${row.label}-${index}`} />
                      ))}
                      <div
                        className={`h-3.5 self-center rounded-full shadow-[0_8px_16px_rgba(15,118,110,0.12)] ${
                          row.tone === "teal"
                            ? "bg-[linear-gradient(90deg,_#0f766e,_#2dd4bf)]"
                            : row.tone === "cyan"
                              ? "bg-[linear-gradient(90deg,_#14b8a6,_#5eead4)]"
                              : row.tone === "lime"
                                ? "bg-[linear-gradient(90deg,_#84cc16,_#bef264)]"
                                : "bg-[linear-gradient(90deg,_#fde047,_#facc15)]"
                        }`}
                        data-testid={`quote-preview-planning-bar-${row.label.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                        style={{ gridColumn: `${row.startWeek} / span ${row.spanWeeks}` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-white/92 p-6" data-testid="quote-preview-breakdown-section">
          <h3 className="bim-display text-xl font-semibold text-slate-950">Détail de l’estimation</h3>
          <div className="mt-5 overflow-hidden rounded-[1.2rem] border border-[#e5e7eb]">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-[#f8fcf3] text-left text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em]">Poste</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em]">Jours-homme</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em]">Montant HT</th>
                </tr>
              </thead>
              <tbody>
                {breakdownRows.map((row) => (
                  <tr className="border-t border-[#e5e7eb]" key={row.label}>
                    <td className="px-4 py-4 text-slate-800">{row.label}</td>
                    <td className="px-4 py-4 text-slate-600">{row.days.toFixed(3)} j-h</td>
                    <td className="px-4 py-4 text-slate-600">{money(row.amount)} HT</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#d9e7d5] bg-[#f8fcf3] font-semibold text-slate-950">
                  <td className="px-4 py-4">Total</td>
                  <td className="px-4 py-4">{days.toFixed(3)} j-h</td>
                  <td className="px-4 py-4">{money(totalAmount)} HT</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6" data-testid="quote-preview-mission-scope">
          <h3 className="bim-display text-xl font-semibold text-slate-950">Périmètre de mission inclus</h3>
          <p className="mt-3 text-sm leading-7 text-slate-500">Selon les paramètres sélectionnés, l’estimation peut inclure les prestations suivantes :</p>
          <ul className="mt-5 grid gap-3 pl-5 text-base leading-8 text-slate-700" data-testid="quote-preview-mission-list">
            {missionItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="grid gap-5 lg:grid-cols-3" data-testid="quote-preview-conditions-grid">
          {[
            { title: "Hypothèses", items: assumptionsList, testId: "quote-preview-assumptions" },
            { title: "Exclusions", items: exclusions, testId: "quote-preview-exclusions" },
            { title: "Conditions", items: conditions, testId: "quote-preview-conditions" },
          ].map((group) => (
            <Card className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_18px_40px_-32px_rgba(15,118,110,0.12)]" data-testid={group.testId} key={group.title}>
              <CardContent className="p-6">
                <h3 className="bim-display text-xl font-semibold text-slate-950">{group.title}</h3>
                <ul className="mt-5 grid gap-3 pl-5 text-sm leading-7 text-slate-700">
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="rounded-[1.8rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6" data-testid="quote-preview-next-step">
          <h3 className="bim-display text-xl font-semibold text-slate-950">Prochaine étape</h3>
          <p className="mt-4 text-base leading-8 text-slate-600">Pour transformer cette estimation en devis officiel, Bimcubes analysera les documents transmis : plans, maquettes existantes, CCTP, DPGF, planning ou cahier des charges BIM.</p>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {[
              { title: "Signature Bimcubes", testId: "quote-preview-signature-bimcubes" },
              { title: "Signature Client", testId: "quote-preview-signature-client" },
            ].map((item) => (
              <div key={item.title}>
                <div className="text-sm font-medium text-slate-900">{item.title}</div>
                <div className="mt-3 h-32 rounded-[1.2rem] border border-dashed border-[#cbd5e1] bg-[#f8fcf3]" data-testid={item.testId} />
                <div className="mt-2 text-xs text-slate-500">Nom, date, signature</div>
              </div>
            ))}
          </div>
        </section>

        <Separator className="my-2" />
        <div className="flex flex-wrap items-center justify-end gap-3" data-testid="quote-preview-actions">
          <Button data-testid="quote-preview-close-button" onClick={() => onOpenChange(false)} variant="outline">Fermer</Button>
          <Button data-testid="quote-preview-print-button" onClick={printPdf} variant="outline">Imprimer en PDF</Button>
          <Button className="border-[#d9e7d5] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="quote-preview-download-button" onClick={downloadHtml} variant="outline">
            Télécharger l’offre
          </Button>
          <Button className="bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="quote-preview-request-official-button" onClick={requestOfficialQuote}>
            Demander un devis officiel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};